// frontend/src/components/Settings/PermissionManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupsAPI, permissionsAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import Toast from '../Common/Toast';
import {
  FiShield, FiSave, FiArrowLeft, FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import './Settings.css';

// Operation columns shown in the matrix
const OPS = [
  { key: 'has_permission', label: '👁',    title: 'Visível no menu' },
  { key: 'can_read',       label: 'Ler',   title: 'Leitura / acesso' },
  { key: 'can_create',     label: 'Criar', title: 'Criar registos' },
  { key: 'can_write',      label: 'Editar',title: 'Editar registos' },
  { key: 'can_delete',     label: 'Elim.', title: 'Eliminar registos' },
  { key: 'can_export',     label: 'Exp.',  title: 'Exportar / imprimir' },
  { key: 'can_execute',    label: 'Exec.', title: 'Executar ações especiais' },
];

const emptyOps = () => Object.fromEntries(OPS.map(o => [o.key, false]));

// ── Single permission row ─────────────────────────────────────────────────────
const PermRow = ({ perm, depth, hasChildren, expanded, onToggleExpand, onChange }) => {
  const indent = depth * 20;

  const handleOp = (opKey) => {
    const next = !perm[opKey];
    const updated = { ...perm, [opKey]: next };
    // If turning on any operation, also turn on has_permission (visibility)
    if (next && opKey !== 'has_permission') updated.has_permission = true;
    // If turning off visibility, turn off all
    if (!next && opKey === 'has_permission') {
      OPS.forEach(o => { updated[o.key] = false; });
    }
    onChange(updated);
  };

  // "All" column: check if all ops are true
  const allChecked = OPS.every(o => perm[o.key]);
  const someChecked = OPS.some(o => perm[o.key]);

  const toggleAll = () => {
    const next = !allChecked;
    const updated = { ...perm };
    OPS.forEach(o => { updated[o.key] = next; });
    onChange(updated);
  };

  return (
    <tr style={{ background: perm.has_permission ? '#f0fdf9' : 'white' }}>
      {/* Menu item name */}
      <td style={{ paddingLeft: 12 + indent, fontSize: 13,
        fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 400,
        color: 'var(--gray-900)', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hasChildren ? (
            <button type="button" onClick={onToggleExpand}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, color: 'var(--gray-500)', flexShrink: 0 }}>
              {expanded ? <FiChevronDown size={13}/> : <FiChevronRight size={13}/>}
            </button>
          ) : <span style={{ width: 19, flexShrink: 0 }}/>}
          {perm.icon && <span style={{ fontSize: 14 }}>{perm.icon}</span>}
          <span>{perm.description}</span>
          <code style={{
            fontSize: 10, fontFamily: 'monospace', color: 'var(--gray-400)',
            background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
            borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
          }}>
            {perm.menu_key}
          </code>
        </div>
      </td>

      {/* "All" toggle */}
      <td style={{ textAlign: 'center', padding: '4px 6px' }}>
        <input type="checkbox"
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
          onChange={toggleAll}
          style={{ cursor: 'pointer', accentColor: 'var(--primary)' }}/>
      </td>

      {/* Individual operation checkboxes */}
      {OPS.map(op => (
        <td key={op.key} style={{ textAlign: 'center', padding: '4px 6px' }}>
          <input type="checkbox"
            checked={!!perm[op.key]}
            onChange={() => handleOp(op.key)}
            title={op.title}
            style={{ cursor: 'pointer', accentColor: 'var(--primary)' }}/>
        </td>
      ))}
    </tr>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const PermissionManagement = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t }   = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [group,       setGroup]       = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [hasChanges,  setHasChanges]  = useState(false);
  const [collapsed,   setCollapsed]   = useState({});
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupRes, permRes] = await Promise.all([
        groupsAPI.getById(groupId),
        permissionsAPI.getGroupPermissions(groupId),
      ]);
      if (groupRes.data.success) setGroup(groupRes.data.group);
      if (permRes.data.success)  setPermissions(permRes.data.permissions.map(p => ({
        ...emptyOps(), ...p,
      })));
      setError(null);
    } catch (err) {
      setError(t('permissions.error_loading', 'Erro ao carregar permissões'));
    } finally { setLoading(false); }
  }, [groupId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (menuKey, updated) => {
    setPermissions(prev => prev.map(p => p.menu_key === menuKey ? { ...p, ...updated } : p));
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    setPermissions(prev => prev.map(p => ({ ...p, ...Object.fromEntries(OPS.map(o => [o.key, true])) })));
    setHasChanges(true);
  };
  const handleDeselectAll = () => {
    setPermissions(prev => prev.map(p => ({ ...p, ...emptyOps() })));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await permissionsAPI.updateGroupPermissions(groupId, permissions);
      if (r.data.success) {
        toast_success(t('permissions.saved_success', 'Permissões guardadas'));
        setHasChanges(false);
      }
    } catch (err) {
      toast_error(err.response?.data?.error || t('permissions.error_saving', 'Erro ao guardar'));
    } finally { setSaving(false); }
  };

  const toggleCollapse = (key) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // Build tree
  const childrenOf = (parentKey) =>
    permissions.filter(p => p.parent_key === parentKey);

  const renderRows = (parentKey, depth = 0) => {
    const kids = childrenOf(parentKey);
    return kids.flatMap(perm => {
      const children  = childrenOf(perm.menu_key);
      const isExpanded = !collapsed[perm.menu_key];
      const rows = [
        <PermRow
          key={perm.menu_key}
          perm={perm}
          depth={depth}
          hasChildren={children.length > 0}
          expanded={isExpanded}
          onToggleExpand={() => toggleCollapse(perm.menu_key)}
          onChange={upd => handleChange(perm.menu_key, upd)}
        />,
      ];
      if (children.length > 0 && isExpanded) {
        rows.push(...renderRows(perm.menu_key, depth + 1));
      }
      return rows;
    });
  };

  const totalOn = permissions.filter(p => p.has_permission).length;

  // While searching, ignore the tree/collapse structure and show a flat list
  // of every matching row (by description OR menu_key) — a tree filter would
  // otherwise hide matches nested under a non-matching parent.
  const search = searchTerm.trim().toLowerCase();
  const filteredFlat = search
    ? permissions.filter(p =>
        p.description?.toLowerCase().includes(search) ||
        p.menu_key?.toLowerCase().includes(search))
    : null;

  if (loading) return <div className="settings-page"><div className="loading">A carregar…</div></div>;
  if (error)   return <div className="settings-page"><div className="error"><p>{error}</p><button onClick={loadData} className="btn-primary btn-sm">Tentar novamente</button></div></div>;

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="form-header">
        <div>
          <h2>
            <FiShield size={20} style={{ marginRight: 8, color: 'var(--primary)', verticalAlign: 'middle' }}/>
            Permissões
            {group && <span style={{ color: 'var(--primary)', marginLeft: 8 }}>— {group.group_name}</span>}
          </h2>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>
            {group?.user_count ?? 0} utilizadores · {totalOn}/{permissions.length} itens visíveis
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por nome ou menu_key…"
            style={{
              fontSize: 13, padding: '6px 10px', borderRadius: 6,
              border: '1px solid var(--gray-200)', minWidth: 220,
            }}
          />
          <button className="btn btn-secondary btn-sm" onClick={handleDeselectAll}>Limpar tudo</button>
          <button className="btn btn-secondary btn-sm" onClick={handleSelectAll}>Selecionar tudo</button>
        </div>
      </div>

      {hasChanges && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
          padding: '8px 14px', fontSize: 13, marginBottom: 12, color: '#92400e' }}>
          ⚠️ Alterações por guardar
        </div>
      )}

      {/* Permission matrix table */}
      <div className="form-section" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12,
                fontWeight: 700, color: 'var(--gray-600)', minWidth: 220 }}>
                Item de menu
              </th>
              <th style={thStyle} title="Selecionar / desselecionar tudo">*</th>
              {OPS.map(op => (
                <th key={op.key} style={thStyle} title={op.title}>{op.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFlat
              ? filteredFlat.map(perm => (
                  <PermRow
                    key={perm.menu_key}
                    perm={perm}
                    depth={0}
                    hasChildren={false}
                    expanded={false}
                    onToggleExpand={() => {}}
                    onChange={upd => handleChange(perm.menu_key, upd)}
                  />
                ))
              : renderRows(null)}
            {(filteredFlat ? filteredFlat.length === 0 : permissions.filter(p => !p.parent_key).length === 0) && (
              <tr><td colSpan={OPS.length + 2} style={{ textAlign: 'center',
                padding: 32, color: 'var(--gray-400)' }}>
                {filteredFlat ? 'Sem resultados para essa pesquisa.' : 'Sem itens de menu encontrados.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sticky footer */}
      <div className="perm-footer">
        <button className="btn btn-secondary" onClick={() => navigate('/settings/groups')}
          disabled={saving}>
          <FiArrowLeft size={14}/> Voltar
        </button>
        <button className="btn btn-primary" onClick={handleSave}
          disabled={saving || !hasChanges}>
          <FiSave size={14}/> {saving ? 'A guardar…' : 'Guardar Permissões'}
        </button>
      </div>

      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} onClose={removeToast}/>
        ))}
      </div>
    </div>
  );
};

const thStyle = {
  textAlign: 'center', padding: '10px 8px',
  fontSize: 11, fontWeight: 700, color: 'var(--gray-600)',
  whiteSpace: 'nowrap', minWidth: 52,
};

export default PermissionManagement;